import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { OutlookRecipient, ResourceType } from "../types/resource";

interface ResourceState {
  // Available and selected resources (master data)
  availableResource: ResourceType[];
  selectedResource: ResourceType[];
  resources: ResourceType[];
  // Resource management methods
  setAvailableResource: (resources: ResourceType[]) => void;
  setSelectedResource: (resources: ResourceType[]) => void;
  addSelectedResource: (resource: ResourceType) => void;
  removeSelectedResource: (resource: ResourceType) => void;
  setResources: (resources: ResourceType[]) => void;
  
  // Utility methods
  clearSelectedResources: () => void;
}

const initialState = {
  availableResource: [],
  selectedResource: [],
  resources: [],
};

export const useResourceStore = create<ResourceState>()(
  devtools((set, get) => ({
    ...initialState,

    setAvailableResource: (availableResource) => {
      console.log("📋 ResourceStore: Setting available resources", availableResource.length);
      set({ availableResource }, false, "setAvailableResource");
    },

    setSelectedResource: (selectedResource) => {
      console.log("🎯 ResourceStore: Setting selected resources", {
        count: selectedResource.length,
        resources: selectedResource.map(r => ({ name: r.Name, email: r.Email, type: r.ResourceType })),
        source: "setSelectedResource (likely from useOfficeEvents)"
      });
      set({ selectedResource }, false, "setSelectedResource");
    },

    addSelectedResource: (resource) => {
      console.log("➕ ResourceStore: Adding selected resource", resource.Name, resource.Email);
      set((state) => {
        // Enhanced duplicate checking with detailed logging
        const existsByEmail = state.selectedResource.find(r => r.Email.toLowerCase() === resource.Email.toLowerCase());
        const existsByName = state.selectedResource.find(r => r.Name.toLowerCase() === resource.Name.toLowerCase());
        
        if (existsByEmail) {
          console.warn("⚠️ DUPLICATE DETECTED: Resource already selected by EMAIL:", {
            incoming: { name: resource.Name, email: resource.Email },
            existing: { name: existsByEmail.Name, email: existsByEmail.Email }
          });
          return state;
        }
        
        if (existsByName) {
          console.warn("⚠️ DUPLICATE DETECTED: Resource already selected by NAME:", {
            incoming: { name: resource.Name, email: resource.Email },
            existing: { name: existsByName.Name, email: existsByName.Email }
          });
          return state;
        }

        // Remove from available resources
        const newAvailable = state.availableResource.filter(r => r.Email.toLowerCase() !== resource.Email.toLowerCase());
        const newSelected = [...state.selectedResource, resource];

        console.log("✅ ResourceStore: Resource added successfully", {
          resourceName: resource.Name,
          newSelectedCount: newSelected.length,
          newAvailableCount: newAvailable.length
        });

        return {
          availableResource: newAvailable,
          selectedResource: newSelected
        };
      }, false, "addSelectedResource");
    },

    removeSelectedResource: (resource) => {
      console.log("➖ ResourceStore: Removing selected resource", resource.Name);
      set((state) => {
        // Remove from selected resources
        const newSelected = state.selectedResource.filter(r => r.Email.toLowerCase() !== resource.Email.toLowerCase());
        
        // Add back to available resources if not already there
        const exists = state.availableResource.some(r => r.Email.toLowerCase() === resource.Email.toLowerCase());
        const newAvailable = exists ? state.availableResource : [...state.availableResource, resource];

        return {
          availableResource: newAvailable,
          selectedResource: newSelected
        };
      }, false, "removeSelectedResource");
    },

    setResources: (resources) => {
      console.log("🎯 ResourceStore: Setting resources", resources.length);
      set({ resources }, false, "setResources");
    },

    // Utility methods
    clearSelectedResources: () => {
      console.log("🧹 ResourceStore: Clearing selected resources");
      set((state) => ({
        availableResource: [...state.availableResource, ...state.selectedResource],
        selectedResource: []
      }), false, "clearSelectedResources");
    },
  }), {
    name: 'resource-store',
  })
);
